<template>
    <div class="announcement-bar" v-if="visibleBanners.length" ref="bar">
        <div
            v-for="banner in visibleBanners"
            :key="banner.id"
            class="alert alert-dismissible fade show mb-0 rounded-0 border-0 text-center py-2 fw-medium"
            :class="bannerClasses(banner.type_color)"
            role="alert"
        >
            <strong v-if="banner.title">{{ banner.title }} : </strong>
            <span v-html="renderMessage(banner.message)"></span>
            <button
                type="button"
                class="btn-close"
                :class="{ 'btn-close-white': hasLightText(banner.type_color) }"
                aria-label="Fermer"
                @click="dismiss(banner.id)"
            ></button>
        </div>
    </div>
</template>

<script>
import { useAnnouncementStore } from "../Store/AnnouncementStore.js";
import { mapActions, mapState } from "pinia";

const STORAGE_KEY = "dismissed_announcements";

export default {
    name: "AnnouncementBanner",
    data() {
        return {
            dismissedIds: this.loadDismissed(),
            resizeObserver: null,
        };
    },
    created() {
        this.fetchActiveBanners();

        Echo.channel("announcements").listen("AnnouncementUpdatedEvent", () => {
            this.fetchActiveBanners();
        });
    },
    computed: {
        ...mapState(useAnnouncementStore, ["banners"]),
        visibleBanners() {
            return this.banners.filter((banner) => !this.dismissedIds.includes(banner.id));
        },
    },
    methods: {
        ...mapActions(useAnnouncementStore, ["fetchActiveBanners"]),
        hasLightText(color) {
            return color !== "warning";
        },
        bannerClasses(color) {
            return [`bg-${color}`, this.hasLightText(color) ? "text-white" : "text-dark"];
        },
        escapeHtml(value) {
            return String(value ?? "")
                .replace(/&/g, "&amp;")
                .replace(/</g, "&lt;")
                .replace(/>/g, "&gt;")
                .replace(/"/g, "&quot;")
                .replace(/'/g, "&#39;");
        },
        renderMessage(message) {
            let html = this.escapeHtml(message);

            // Liens markdown : [libellé](https://...)
            html = html.replace(
                /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g,
                (match, label, url) => `<a href="${url}" target="_blank" rel="noopener noreferrer">${label}</a>`
            );

            // URLs nues, hors liens déjà générés
            html = html
                .split(/(<a\b[^>]*>.*?<\/a>)/g)
                .map((part) => {
                    if (part.startsWith("<a")) {
                        return part;
                    }
                    return part.replace(
                        /(https?:\/\/[^\s<]+)/g,
                        (url) => `<a href="${url}" target="_blank" rel="noopener noreferrer">${url}</a>`
                    );
                })
                .join("");

            return html;
        },
        loadDismissed() {
            try {
                return JSON.parse(localStorage.getItem(STORAGE_KEY)) || [];
            } catch {
                return [];
            }
        },
        dismiss(id) {
            if (!this.dismissedIds.includes(id)) {
                this.dismissedIds.push(id);
                localStorage.setItem(STORAGE_KEY, JSON.stringify(this.dismissedIds));
            }
        },
        updateOffset() {
            this.$nextTick(() => {
                const bar = this.$refs.bar;
                const height = bar ? bar.offsetHeight : 0;

                if (height > 0) {
                    document.body.style.setProperty("--vz-announcement-height", `${height}px`);
                    document.body.classList.add("has-announcement");
                } else {
                    document.body.style.removeProperty("--vz-announcement-height");
                    document.body.classList.remove("has-announcement");
                }
            });
        },
    },
    watch: {
        visibleBanners() {
            this.updateOffset();
        },
    },
    mounted() {
        this.updateOffset();
        this.resizeObserver = new ResizeObserver(() => this.updateOffset());
        if (this.$refs.bar) {
            this.resizeObserver.observe(this.$refs.bar);
        }
    },
    updated() {
        if (this.resizeObserver && this.$refs.bar) {
            this.resizeObserver.observe(this.$refs.bar);
        }
    },
    beforeUnmount() {
        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
        }
        Echo.leaveChannel("announcements");
        document.body.style.removeProperty("--vz-announcement-height");
        document.body.classList.remove("has-announcement");
    },
};
</script>

<style>
.announcement-bar {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    z-index: 1005;
}

.announcement-bar .alert {
    box-shadow: 0 1px 3px rgba(56, 65, 74, 0.15);
}

.announcement-bar .alert a {
    color: inherit;
    text-decoration: underline;
    font-weight: 700;
}

.announcement-bar .alert-dismissible .btn-close {
    top: 50%;
    transform: translateY(-50%);
    padding-top: 0;
    padding-bottom: 0;
}

body.has-announcement #page-topbar {
    top: var(--vz-announcement-height, 0px);
}

body.has-announcement .app-menu {
    top: var(--vz-announcement-height, 0px);
}

body.has-announcement .main-content {
    padding-top: var(--vz-announcement-height, 0px);
}
</style>
